import {
  IDS_DESCENSO,
  IDS_INTENSOS_AGARRE,
  IDS_INVERSION_AEREA,
  IDS_TREPA,
  ORDEN_NIVEL
} from '../data/constantes'
import type { Clase, NivelClase, Recurso } from '../types'
import { minutosPlanificados } from './clase'

export type TipoAdvertencia = 'alerta' | 'aviso' | 'info'

export interface Advertencia {
  id: string
  tipo: TipoAdvertencia
  titulo: string
  detalle: string
}

export function nivelSuperaClase(recurso: Recurso, nivel: NivelClase): boolean {
  if (nivel === 'Multinivel') return false
  return ORDEN_NIVEL[recurso.nivelMinimo] > ORDEN_NIVEL[nivel]
}

export const MENSAJE_NIVEL_SUPERIOR =
  'Este recurso está clasificado por encima del nivel general elegido. Revisá sus prerrequisitos y decidí si corresponde para este grupo.'

export function estadoTiempo(clase: Clase): {
  planificado: number
  objetivo: number
  diferencia: number
  estado: 'ok' | 'atencion' | 'excedido'
  texto: string
} {
  const planificado = minutosPlanificados(clase)
  const objetivo = clase.duracion
  const diferencia = planificado - objetivo
  let estado: 'ok' | 'atencion' | 'excedido' = 'ok'
  let texto = ''
  if (diferencia > 5) {
    estado = 'excedido'
    texto = `Excede la duración por ${diferencia} min`
  } else if (diferencia > 0) {
    estado = 'atencion'
    texto = `Excede la duración por ${diferencia} min`
  } else if (diferencia < -5) {
    estado = 'atencion'
    texto = `Faltan ${Math.abs(diferencia)} min por planificar`
  } else {
    estado = 'ok'
    texto = diferencia === 0 ? 'Tiempo ajustado' : `Faltan ${Math.abs(diferencia)} min por planificar`
  }
  return { planificado, objetivo, diferencia, estado, texto }
}

export function calcularAdvertencias(clase: Clase, recursos: Recurso[]): Advertencia[] {
  const porId = new Map(recursos.map((r) => [r.id, r]))
  const asignados = clase.bloques.flatMap((b) => b.ejercicios)
  const ids = asignados.map((e) => e.recursoId).filter((x): x is string => Boolean(x))
  const usados = ids.map((id) => porId.get(id)).filter((r): r is Recurso => Boolean(r))
  const lista: Advertencia[] = []

  if (clase.participantes > clase.puntos) {
    lista.push({
      id: 'participantes',
      tipo: 'aviso',
      titulo: 'Más participantes que puntos de trabajo',
      detalle:
        'Hay más participantes que puntos de trabajo. Sumá una estrategia de rotación o estaciones.'
    })
  }

  const hayTrepa = ids.some((id) => IDS_TREPA.includes(id))
  const hayDescenso =
    ids.some((id) => IDS_DESCENSO.includes(id)) ||
    asignados.some(
      (e) => !e.recursoId && /descens|bajad/i.test(`${e.nombre} ${e.notas}`)
    )
  if (hayTrepa && !hayDescenso) {
    lista.push({
      id: 'trepa-descenso',
      tipo: 'alerta',
      titulo: 'Trepa sin descenso vinculado',
      detalle:
        'Agregá T07 Descenso básico controlado, T08 Descenso segmentado con pausas o un descenso personalizado.'
    })
  }

  usados
    .filter((r) => IDS_INVERSION_AEREA.includes(r.id))
    .forEach((r) => {
      lista.push({
        id: `inversion-${r.id}`,
        tipo: 'aviso',
        titulo: `${r.id} ${r.nombre}: revisá prerrequisitos`,
        detalle: `Prerrequisitos: ${r.prerrequisitos.join(' · ')}. ${r.seguridad}`
      })
    })

  const bilateralSinLado = asignados.filter((e) => {
    const r = e.recursoId ? porId.get(e.recursoId) : null
    return r?.bilateral && e.lado === 'No aplica'
  })
  if (bilateralSinLado.length > 0) {
    lista.push({
      id: 'lateralidad',
      tipo: 'aviso',
      titulo: 'Falta indicar lado en habilidades bilaterales',
      detalle: `Definí el lado en: ${bilateralSinLado.map((e) => e.nombre).join(', ')}.`
    })
  }

  const intensos = ids.filter((id) => IDS_INTENSOS_AGARRE.includes(id))
  if (intensos.length >= 3) {
    lista.push({
      id: 'fatiga',
      tipo: 'aviso',
      titulo: 'Posible fatiga de agarre y tracción',
      detalle: `Hay ${intensos.length} recursos intensos de agarre o tracción (${intensos.join(', ')}). Revisá el orden, las pausas y el descenso final.`
    })
  }

  if (clase.nivel === 'Multinivel') {
    const objetivoPrincipal = clase.bloques.find((b) => b.nombre === 'Objetivo principal')
    const variaciones = new Set(objetivoPrincipal?.ejercicios.map((e) => e.variacion) || [])
    const faltan = ['Regresión', 'Base', 'Progresión'].filter((v) => !variaciones.has(v as never))
    if (faltan.length > 0) {
      lista.push({
        id: 'multinivel',
        tipo: 'alerta',
        titulo: 'Multinivel incompleto',
        detalle: `El objetivo principal necesita Regresión, Base y Progresión. Falta: ${faltan.join(', ')}.`
      })
    }
    if (clase.logistica.estrategia !== 'Estaciones' && clase.logistica.estrategia !== 'Rotaciones') {
      lista.push({
        id: 'multinivel-org',
        tipo: 'aviso',
        titulo: 'Definí la organización de puntos',
        detalle:
          'En Multinivel conviene una estrategia de estaciones o rotaciones y dejar anotado qué grupo necesita observación directa.'
      })
    }
  }

  if (clase.nivel === 'Introducción' && (clase.altura === 'Altura' || clase.altura === 'Mixta')) {
    lista.push({
      id: 'intro-altura',
      tipo: 'aviso',
      titulo: 'Altura en un grupo de Introducción',
      detalle:
        'En Introducción conviene priorizar Piso, Nudo y Baja altura. Revisá si la altura elegida corresponde a este grupo.'
    })
  }

  if (usados.some((r) => r.familia === 'Figuras estáticas')) {
    lista.push({
      id: 'figuras',
      tipo: 'info',
      titulo: 'Figuras en la clase',
      detalle: 'Recordá definir entrada, salida y asistencia para cada figura nueva.'
    })
  }

  usados
    .filter((r) => nivelSuperaClase(r, clase.nivel))
    .forEach((r) => {
      lista.push({
        id: `nivel-${r.id}`,
        tipo: 'aviso',
        titulo: `${r.id} ${r.nombre} está por encima del nivel elegido`,
        detalle: MENSAJE_NIVEL_SUPERIOR
      })
    })

  usados
    .filter((r) => r.personalizado && r.dinamico)
    .forEach((r) => {
      lista.push({
        id: `dinamico-${r.id}`,
        tipo: 'alerta',
        titulo: `${r.nombre} es contenido dinámico o caída`,
        detalle:
          'Verificá preparación, salida, asistencia y condiciones de seguridad antes de la clase.'
      })
    })

  const tiempo = estadoTiempo(clase)
  if (tiempo.estado === 'excedido') {
    lista.push({
      id: 'tiempo',
      tipo: 'aviso',
      titulo: 'La clase excede la duración prevista',
      detalle: `${tiempo.planificado} min planificados sobre ${tiempo.objetivo} min.`
    })
  }

  const sinConfirmar = Object.values(clase.logistica.checks).filter((v) => !v).length
  if (sinConfirmar > 0) {
    lista.push({
      id: 'logistica',
      tipo: 'aviso',
      titulo: 'Controles de espacio sin confirmar',
      detalle: `Quedaron ${sinConfirmar} controles sin confirmar. Revisalos antes de comenzar la clase.`
    })
  }

  return lista
}
